import React from 'react';
import Card from '../components/ProfileCard.tsx';
import StatCard from '../components/StatCard';
import BarChart from '../components/charts/BarChart';
import LineChart from '../components/charts/LineChart';
import PieChart from '../components/charts/PieChart';
import AreaChart from '../components/charts/AreaChart';
import { ShoppingCart, Users, CreditCard, TrendingUp, Package } from 'lucide-react';

const Dashboard: React.FC = () => {
    // Sample data for charts
    const salesData = [
        { month: 'Jan', sales: 4000 },
        { month: 'Feb', sales: 3000 },
        { month: 'Mar', sales: 5000 },
        { month: 'Apr', sales: 2780 },
        { month: 'May', sales: 1890 },
        { month: 'Jun', sales: 2390 },
        { month: 'Jul', sales: 3490 },
    ];

    const userActivityData = [
        { day: 'Mon', active: 2400 },
        { day: 'Tue', active: 1398 },
        { day: 'Wed', active: 9800 },
        { day: 'Thu', active: 3908 },
        { day: 'Fri', active: 4800 },
        { day: 'Sat', active: 3800 },
        { day: 'Sun', active: 4300 },
    ];

    const categoryData = [
        { name: 'E-commerce', value: 400 },
        { name: 'Blog', value: 300 },
        { name: 'Portfolio', value: 300 },
        { name: 'Landing Page', value: 200 },
        { name: 'Admin', value: 100 },
    ];

    const revenueData = [
        { month: 'Jan', revenue: 4000 },
        { month: 'Feb', revenue: 3000 },
        { month: 'Mar', revenue: 5000 },
        { month: 'Apr', revenue: 2780 },
        { month: 'May', revenue: 1890 },
        { month: 'Jun', revenue: 2390 },
        { month: 'Jul', revenue: 3490 },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Overview of your template marketplace performance
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title="Total Sales"
                    value="$24,780"
                    icon={<ShoppingCart size={24} />}
                    change={{ value: '12%', type: 'increase' }}
                />
                <StatCard
                    title="Active Users"
                    value="573"
                    icon={<Users size={24} />}
                    change={{ value: '8%', type: 'increase' }}
                />
                <StatCard
                    title="Revenue"
                    value="$18,230"
                    icon={<CreditCard size={24} />}
                    change={{ value: '7%', type: 'increase' }}
                />
                <StatCard
                    title="Conversion Rate"
                    value="3.2%"
                    icon={<TrendingUp size={24} />}
                    change={{ value: '0.5%', type: 'decrease' }}
                />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card title="Monthly Sales">
                    <BarChart data={salesData} xKey="month" yKey="sales" />
                </Card>
                <Card title="User Activity">
                    <LineChart data={userActivityData} xKey="day" yKey="active" />
                </Card>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card title="Popular Template Categories">
                    <PieChart data={categoryData} nameKey="name" dataKey="value" />
                </Card>
                <Card title="Revenue Trend">
                    <AreaChart data={revenueData} xKey="month" yKey="revenue" />
                </Card>
            </div>

            <div className="grid grid-cols-1 gap-6">
                <Card title="Recent Template Sales">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Template
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Customer
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Price
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {[
                                {
                                    template: 'E-commerce Pro',
                                    customer: 'John Smith',
                                    price: '$59',
                                    date: '2025-03-15',
                                    status: 'Completed',
                                },
                                {
                                    template: 'Blog Master',
                                    customer: 'Sarah Johnson',
                                    price: '$39',
                                    date: '2025-03-14',
                                    status: 'Completed',
                                },
                                {
                                    template: 'Portfolio Plus',
                                    customer: 'Michael Brown',
                                    price: '$49',
                                    date: '2025-03-14',
                                    status: 'Completed',
                                },
                                {
                                    template: 'Admin Dashboard',
                                    customer: 'Emily Davis',
                                    price: '$79',
                                    date: '2025-03-13',
                                    status: 'Completed',
                                },
                                {
                                    template: 'Landing Page Pro',
                                    customer: 'David Wilson',
                                    price: '$29',
                                    date: '2025-03-12',
                                    status: 'Completed',
                                },
                            ].map((sale, index) => (
                                <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-md flex items-center justify-center">
                                                <Package size={20} className="text-gray-500" />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {sale.template}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{sale.customer}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{sale.price}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{sale.date}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {sale.status}
                      </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;